import { createRatriderAnimator } from './ratrider-animator.js';
import type * as THREE from 'three';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { createCharacterModel } from './character-model.js';
import { createCharacterAnimator } from './create-character-animator.js';
import { WolfAnimator } from './wolf-animator.js';
import { ShadowAnimator } from './shadow-animator.js';
import type { GameplayUnitAnimator, UnitAnimator } from './unit-animator.js';
import { humanoidDeathRig, wolfDeathRig, type DeathRig } from './death-rig.js';
import { useUnitDeathPhysics } from './unit-death.js';
import { useUnitHitPhysics } from './hit-springs.js';
import { graphicsOptions, type CharacterDetail } from '../graphics-options.js';
import type { HeadDetail } from './human-head.js';

/** What the scene needs of any figure, whatever its skeleton. */
export interface UnitFigure { root: THREE.Group; height: number }

/**
 * Builds the figure and animator for a unit. This is the only place that knows which families exist: humans get the animator of their own
 * body (`createCharacterAnimator`), the wolf its clip player, the Shadow its veil. Every family answers to the same `UnitAnimator` contract.
 */
/** The head mesh detail each Character detail setting draws figures in the world with. */
export const FIGURE_DETAIL: Record<CharacterDetail, HeadDetail> = { high: 'high', low: 'low' };
/** World figures at Low Character detail also sample their bodies and clothes coarsely (`HumanModelOptions.coarse`). */
export const coarseFigure = (detail: HeadDetail) => detail === 'low';
/** How world figures (residents, enemies, heroes in play) are built at a detail level; figures are baked with these too (`figure-bakes.ts`). */
export const worldFigureOptions = (detail: HeadDetail) => ({ palette: true, detail, coarse: coarseFigure(detail) });

/** A rat rider is built from its goblin. */
const riderDefinition = (definition: UnitDefinition): UnitDefinition =>
  ({ ...definition, appearance: { ...definition.appearance!, rig: definition.appearance!.parts?.['rider-body'] === 'goblin-female' ? 'goblin-female' : 'goblin-male' } });
/** The definition whose sculpted head a unit's figure wears, or null for bodies without one (wolves, Shadows). */
export function headedDefinition(definition: UnitDefinition): UnitDefinition | null {
  const rig = definition.appearance?.rig;
  return rig === 'shadow' || rig === 'wolf' ? null : rig === 'goblin-ratrider' ? riderDefinition(definition) : definition;
}

export function createUnitAnimator(definition: UnitDefinition, detail = FIGURE_DETAIL[graphicsOptions.characterDetail]): { animator: GameplayUnitAnimator; rig: UnitFigure } {
  // A Shadow has no bones to fall or flinch: it dissolves and recoils on its own.
  if (definition.appearance?.rig === 'shadow') { const shadow = new ShadowAnimator(definition); return { animator: shadow, rig: shadow.rig }; }
  let figure: { animator: UnitAnimator; rig: UnitFigure; deathRig: DeathRig; loose?: THREE.Object3D[] };
  if (definition.appearance?.rig === 'goblin-ratrider') {
    figure = createRatriderAnimator(riderDefinition(definition), detail);
  } else if (definition.appearance?.rig === 'wolf') {
    const wolf = new WolfAnimator(definition);
    // The tail and ears swing loose under a blow.
    const loose = ['tail_1', 'tail_2', 'tail_3', 'ear_L', 'ear_R'].map(name => wolf.rig.joints.get(name)).filter(bone => bone !== undefined);
    figure = { animator: wolf, rig: wolf.rig, deathRig: wolfDeathRig(wolf.rig), loose };
  } else {
    const rig = createCharacterModel(definition, worldFigureOptions(detail));
    figure = { animator: createCharacterAnimator(rig, definition), rig, deathRig: humanoidDeathRig(rig) };
  }
  // All branches converge here. Death owns the outer update so it captures the displayed hit pose before living springs restore it.
  return { animator: useUnitDeathPhysics(useUnitHitPhysics(figure.animator, figure.deathRig, figure.loose), figure.deathRig), rig: figure.rig };
}
